import React from 'react';
import { Spinner, Stack, Text } from '@billda/design-system';

interface ListViewShellProps {
  itemCount: number;
  loading: boolean;
  emptyMessage: string;
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyHelperText?: string;
  columnHeaders?: React.ReactNode;
  children: React.ReactNode;
  'data-testid'?: string;
}

export const ListViewShell: React.FC<ListViewShellProps> = ({
  itemCount,
  loading,
  emptyMessage,
  emptyIcon,
  emptyTitle,
  emptyHelperText,
  columnHeaders,
  children,
  'data-testid': testId,
}) => {
  if (loading && itemCount === 0) {
    return (
      <div className="flex justify-center py-6">
        <Spinner size={24} />
      </div>
    );
  }

  if (itemCount === 0) {
    if (emptyIcon || emptyTitle || emptyHelperText) {
      return (
        <div
          className="flex min-h-[300px] items-center justify-center p-4"
          data-testid="list-empty-state"
        >
          <Stack spacing={2} alignItems="center" className="max-w-[400px]">
            {emptyIcon}
            {emptyTitle && <Text variant="h6">{emptyTitle}</Text>}
            {emptyHelperText && (
              <Text variant="body2" color="secondary" className="text-center">
                {emptyHelperText}
              </Text>
            )}
          </Stack>
        </div>
      );
    }

    return (
      <Text variant="body2" color="secondary" className="py-6 text-center">
        {emptyMessage}
      </Text>
    );
  }

  return (
    <div data-testid={testId}>
      {columnHeaders && (
        <div className="flex items-center gap-3 border-b border-border-default px-2 py-1.5 sm:px-3">
          {columnHeaders}
        </div>
      )}

      <div className="[&>*]:border-b [&>*]:border-border-default">
        {children}
      </div>
    </div>
  );
};
